import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Move, Plus, Pencil, Trash2, FileText, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import {
  CaseSheetDoc,
  DesignerSurface,
  DischargeSummaryDoc,
  PAPER_MM,
  PrescriptionDoc,
  RxPad,
  defaultLayout,
  defaultPrescriptionFieldLayout,
  PRESCRIPTION_PAD_FIELDS,
} from "@/components/PrintDoc";
import { titleize } from "@/lib/format";

const FIELD_LABEL = (k) => titleize(k);

const PRESCRIPTION_TYPES = ["opd_prescription", "consultation_prescription"];
const ZONE_META = [["patient", "Patient details"], ["clinical", "Diagnosis / clinical"],
  ["treatment", "Treatment / medicines"], ["investigations", "Investigations / tests"],
  ["signature", "Authoriser signature"]];
const NOW = new Date().toISOString();
const PLUS5 = new Date(Date.now() + 5 * 86400000).toISOString();
const SAMPLE_MEDS = [
  { name: "Tab Paracetamol", strength: "500 mg", dosage: "1 tab", frequency: "1-0-1", duration: "5 days", instructions: "After food" },
  { name: "Cap Amoxicillin", strength: "500 mg", dosage: "1 cap", frequency: "1-1-1", duration: "7 days", instructions: "After food" },
  { name: "Syp Ascoril", strength: "100 ml", dosage: "10 ml", frequency: "0-0-1", duration: "5 days", instructions: "At bedtime" },
];
const PERSON = { patient_name: "Rohan Mehta", patient_uhid: "UHID-24-0092", patient_age: 34, patient_gender: "male",
  patient_phone: "+91 98123 45670", doctor_name: "Anjali Deshpande", doctor_qualification: "MBBS, MD",
  doctor_registration: "MMC/2011/45872", department_name: "General Medicine" };
function sampleRx(mode, docType, business) {
  const base = { encounter_type: docType === "consultation_prescription" ? "consultation" : "opd", created_at: NOW, prescription_no: "RX-000128" };
  if (mode === "blank") return { business, rx: { ...base, medicines: [] }, patient: {} };
  if (mode === "partial") return { business, rx: { ...base, ...PERSON, medicines: [] }, patient: { blood_group: "O+", age: 34, gender: "male" } };
  return { business, rx: { ...base, ...PERSON, diagnosis: "Acute viral pharyngitis with low-grade fever. Advised rest and adequate hydration.",
    advice: "Warm saline gargles twice daily. Review if fever persists beyond 3 days.", follow_up_date: PLUS5, medicines: SAMPLE_MEDS,
    investigations: ["CBC", "CRP", "Chest X-ray PA view"] },
    patient: { blood_group: "O+", age: 34, gender: "male" } };
}

const SAMPLE_CASE = {
  admission: {
    admission_id: "ADM-000128",
    patient_name: "Rohan Mehta",
    patient_uhid: "UHID-24-0092",
    patient_phone: "+91 98123 45670",
    doctor_name: "Anjali Deshpande",
    ward_name: "General Ward",
    bed_number: "G-12",
    admitted_at: NOW,
    discharged_at: NOW,
    days: 2,
    diagnosis: "Acute viral pharyngitis with low-grade fever.",
    discharge_diagnosis: "Acute viral pharyngitis — improved.",
    treatment_plan: "Supportive care, hydration and monitoring.",
    hospital_course: "Symptoms improved with conservative treatment.",
    procedures: "No procedure performed.",
    condition_at_discharge: "Stable and ambulatory.",
    discharge_medicines: "Paracetamol 500 mg as required for fever.",
    follow_up_date: PLUS5,
    follow_up_instructions: "Review if fever persists or symptoms worsen.",
    summary: "Discharged in a stable condition.",
    services: [],
    case_notes: [],
  },
  patient: { age: 34, gender: "male", blood_group: "O+" },
};

const readImage = (file, cb, maxKb = 900) => {
  if (!file) return;
  if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return toast.error("Upload a PNG, JPG or WEBP image.");
  if (file.size > maxKb * 1024) return toast.error(`Image must be under ${maxKb} KB.`);
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
};

const empty = (module, docTypes, allFields = []) => ({
  module, doc_type: docTypes[0]?.key || "", name: "", logo_image: "", letterhead_image: "",
  header_text: "", footer_text: "", signatory_name: "", signatory_designation: "",
  paper_size: "A4",
  prescription_mode: "digital",
  use_background: false,
  layout: defaultLayout("A4"),
  field_layout: defaultPrescriptionFieldLayout("A4"),
  font_family: "clinical", font_size_pt: 11,
  show_qr: module === "lab",
  fields: Object.fromEntries(allFields.map((k) => [k, true])),
  active: true,
});

// module: "hospital" | "lab" | "pharmacy"
export default function TemplatesManager({ module, business }) {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [activePadField, setActivePadField] = useState("patient_name");

  const load = useCallback(async () => {
    try {
      const [m, list] = await Promise.all([
        api.get("/templates/fields", { params: { module } }),
        api.get("/templates", { params: { module } }),
      ]);
      setMeta(m.data);
      setItems(list.data.items);
    } catch (e) { toast.error(apiError(e)); }
  }, [module]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditing(null);
    setActivePadField("patient_name");
    setForm(empty(module, meta.doc_types, meta.fields));
    setOpen(true);
  };
  const startEdit = (t) => {
    const fallback = empty(module, meta.doc_types, meta.fields);
    setEditing(t.template_id);
    setActivePadField("patient_name");
    setForm({
      ...fallback,
      ...t,
      field_layout: {
        ...defaultPrescriptionFieldLayout(t.paper_size || "A4"),
        ...(t.field_layout || {}),
      },
    });
    setOpen(true);
  };

  const uploadLetterhead = async (file) => {
    if (!file) return;
    const allowedImage = /^image\/(png|jpe?g|webp)$/.test(file.type);
    if (!allowedImage && file.type !== "application/pdf") {
      toast.error("Upload a single-page PDF, PNG, JPG or WEBP background image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Background image must be under 5 MB.");
      return;
    }
    try {
      const payload = new FormData();
      payload.append("file", file);
      payload.append("module", module);
      const { data } = await api.post("/templates/assets", payload);
      setForm((current) => ({
        ...current,
        letterhead_image: data.asset_url,
        letterhead_asset_id: data.asset_id,
      }));
      toast.success("Background uploaded and ready to save with this template.");
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/templates/${editing}`, form);
      else await api.post("/templates", form);
      toast.success(editing ? "Template updated." : "Template created.");
      setOpen(false); setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/templates/${t.template_id}`); toast.success("Template deleted."); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const updatePadField = (key, patch) => {
    setForm((current) => ({
      ...current,
      field_layout: {
        ...current.field_layout,
        [key]: {
          ...defaultPrescriptionFieldLayout(current.paper_size)[key],
          ...current.field_layout?.[key],
          ...patch,
        },
      },
    }));
  };

  if (!meta || !items) return <Loading />;
  const docLabel = (k) => meta.doc_types.find((d) => d.key === k)?.label || k;

  return (
    <div data-testid={`templates-${module}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 max-w-2xl">
          {module === "hospital"
            ? "Configure per-hospital prescription & clinical document templates. Upload a letterhead/logo and choose which patient, doctor and clinical fields appear. Templates are used when generating the document and never shared with another business."
            : module === "lab"
              ? "Configure lab report letterheads, typography, result sections, signatory, footer and QR verification. The active template is applied to the generated report PDF."
              : "Configure pharmacy invoice and receipt branding, typography, fields, signatory and footer. The active template is applied to invoice previews and printouts."}
        </p>
        {meta.can_manage && (
          <button onClick={startCreate} className={primaryBtnCls} data-testid={`add-template-${module}`}>
            <Plus className="h-4 w-4" /> New template
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="panel"><EmptyState title="No templates yet"
          subtitle="Create your first template to brand your documents." testid={`templates-empty-${module}`} /></div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid={`templates-list-${module}`}>
          {items.map((t) => (
            <div key={t.template_id} className="panel p-5" data-testid={`template-card-${t.template_id}`}>
              <div className="flex items-start justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100 text-slate-600">
                  <FileText className="h-5 w-5" strokeWidth={1.7} />
                </div>
                {t.active && <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span>}
              </div>
              <p className="font-ui text-base font-bold text-slate-900 mt-3">{t.name}</p>
              <p className="text-xs text-slate-500 mt-1">{docLabel(t.doc_type)}</p>
              {t.logo_image && <img src={t.logo_image} alt="logo" className="mt-3 h-8 object-contain" />}
              {meta.can_manage && (
                <div className="mt-4 flex gap-2">
                  <button onClick={() => startEdit(t)} className={ghostBtnCls} data-testid={`edit-template-${t.template_id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                  <button onClick={() => remove(t)} className={`${ghostBtnCls} text-red-600 hover:border-red-400`} data-testid={`delete-template-${t.template_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} wide testid={`template-modal-${module}`}
        title={editing ? "Edit template" : "New template"}
        subtitle="Upload a letterhead/logo and map the dynamic fields to include.">
        {form && (
          <form onSubmit={save} className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Template name *">
              <input required className={inputCls} value={form.name} data-testid="template-name-input"
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Document type *">
              <select required className={inputCls} value={form.doc_type} data-testid="template-doctype-select"
                onChange={(e) => setForm({ ...form, doc_type: e.target.value })}>
                {meta.doc_types.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
              </select>
            </Field>
            <Field label="Logo (PNG/JPG/WEBP)">
              <input type="file" accept="image/*" className="text-sm text-slate-700" data-testid="template-logo-input"
                onChange={(e) => readImage(e.target.files[0], (d) => setForm({ ...form, logo_image: d }))} />
              {form.logo_image && <img src={form.logo_image} alt="logo" className="mt-2 h-10 object-contain" />}
            </Field>
            <Field label="Letterhead / background (optional)">
              <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="text-sm text-slate-700" data-testid="template-letterhead-input"
                onChange={(e) => uploadLetterhead(e.target.files[0])} />
              {form.letterhead_image && <img src={form.letterhead_image} alt="letterhead" className="mt-2 h-10 object-contain" />}
            </Field>
            <Field label="Header text"><input className={inputCls} value={form.header_text} onChange={(e) => setForm({ ...form, header_text: e.target.value })} /></Field>
            <Field label="Footer text"><input className={inputCls} value={form.footer_text} onChange={(e) => setForm({ ...form, footer_text: e.target.value })} /></Field>
            <Field label="Signatory name"><input className={inputCls} value={form.signatory_name} onChange={(e) => setForm({ ...form, signatory_name: e.target.value })} /></Field>
            <Field label="Signatory designation"><input className={inputCls} value={form.signatory_designation} onChange={(e) => setForm({ ...form, signatory_designation: e.target.value })} /></Field>

            <>
              <Field label="Document typeface">
                <select className={inputCls} value={form.font_family}
                  data-testid="template-font-family"
                  onChange={(e) => setForm({ ...form, font_family: e.target.value })}>
                  <option value="clinical">Clinical sans</option>
                  <option value="serif">Traditional serif</option>
                  <option value="modern">Modern sans</option>
                </select>
              </Field>
              <Field label="Document font size">
                <select className={inputCls} value={form.font_size_pt}
                  data-testid="template-font-size"
                  onChange={(e) => setForm({ ...form, font_size_pt: Number(e.target.value) })}>
                  {[9, 10, 11, 12, 13, 14, 15, 16].map((size) => (
                    <option key={size} value={size}>{size} pt</option>
                  ))}
                </select>
              </Field>
            </>

            {module === "hospital" && (
              <>
                <Field label="Paper size">
                  <select className={inputCls} value={form.paper_size} data-testid="template-paper-size"
                    onChange={(e) => setForm({ ...form, paper_size: e.target.value })}>
                    <option value="A4">A4 (210 × 297 mm)</option>
                    <option value="A5">A5 (148 × 210 mm)</option>
                  </select>
                </Field>
                <Field label="Default fill mode">
                  <select className={inputCls} value={form.prescription_mode} data-testid="template-mode"
                    onChange={(e) => setForm({ ...form, prescription_mode: e.target.value })}>
                    <option value="digital">Fully populated (digital)</option>
                    <option value="partial">Partially populated</option>
                    <option value="blank">Blank (handwritten)</option>
                  </select>
                </Field>
              </>
            )}

            {module === "hospital" && (
              <div className="sm:col-span-2 rounded-lg border border-slate-200 p-4" data-testid="pad-alignment">
                <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  <input type="checkbox" data-testid="template-use-background" className="h-4 w-4 accent-[#EA580C]"
                    disabled={!form.letterhead_image}
                    checked={Boolean(form.use_background)}
                    onChange={(e) => setForm({ ...form, use_background: e.target.checked,
                      layout: form.layout?.zones ? form.layout : defaultLayout(form.paper_size) })} />
                  Use uploaded letterhead as the full-page prescription pad
                </label>
                {!form.letterhead_image && (
                  <p className="mt-1 text-[11px] text-amber-600">Upload a letterhead / background image above to enable pad mode.</p>
                )}
                {form.use_background && form.letterhead_image && (() => {
                  const paper = form.paper_size || "A4";
                  const layout = form.layout?.zones ? form.layout : defaultLayout(paper);
                  const setZone = (z, patch) => setForm({ ...form, layout: { ...layout, zones: { ...layout.zones, [z]: { ...layout.zones[z], ...patch } } } });
                  const setM = (k, v) => setForm({ ...form, layout: { ...layout, [k]: v } });
                  return (
                    <div className="mt-4 space-y-3">
                      <p className="text-[11px] text-slate-500">Position the dynamic content in the blank area of your pad. Values are millimetres from the top of the page, so the content stays clear of the pre-printed header and footer.</p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <label className="text-xs text-slate-700">Left margin (mm)
                          <input type="number" min={0} max={100} className={`${inputCls} mt-1`} data-testid="layout-left" value={layout.left_mm} onChange={(e) => setM("left_mm", Number(e.target.value))} /></label>
                        <label className="text-xs text-slate-700">Right margin (mm)
                          <input type="number" min={0} max={100} className={`${inputCls} mt-1`} data-testid="layout-right" value={layout.right_mm} onChange={(e) => setM("right_mm", Number(e.target.value))} /></label>
                      </div>
                      <div className="grid gap-2">
                        {ZONE_META.map(([z, lbl]) => (
                          <div key={z} className="flex items-center gap-3">
                            <label className="flex w-48 shrink-0 items-center gap-2 text-xs text-slate-700">
                              <input type="checkbox" className="h-4 w-4 accent-[#EA580C]" data-testid={`zone-${z}-enabled`}
                                checked={layout.zones[z]?.enabled !== false} onChange={(e) => setZone(z, { enabled: e.target.checked })} />
                              {lbl}
                            </label>
                            <input type="range" min={0} max={PAPER_MM[paper].h} step={1} className="flex-1 accent-[#EA580C]" data-testid={`zone-${z}-top`}
                              value={layout.zones[z]?.top_mm ?? 0} onChange={(e) => setZone(z, { top_mm: Number(e.target.value) })} />
                            <span className="w-14 shrink-0 text-right text-xs tabular-nums text-slate-600">{layout.zones[z]?.top_mm ?? 0} mm</span>
                          </div>
                        ))}
                      </div>
                      <button type="button" className={ghostBtnCls} data-testid="layout-reset" onClick={() => setForm({ ...form, layout: defaultLayout(paper) })}>Reset positions for {paper}</button>
                    </div>
                  );
                })()}
              </div>
            )}

            {module === "hospital" && PRESCRIPTION_TYPES.includes(form.doc_type) && (() => {
              const selected = PRESCRIPTION_PAD_FIELDS.find((item) => item.key === activePadField)
                || PRESCRIPTION_PAD_FIELDS[0];
              const field = {
                ...defaultPrescriptionFieldLayout(form.paper_size)[selected.key],
                ...(form.field_layout?.[selected.key] || {}),
              };
              const numberField = (label, key, min, max) => (
                <label className="min-w-0 text-xs text-slate-700">
                  {label}
                  <input
                    type="number"
                    min={min}
                    max={max}
                    step="0.5"
                    className={`${inputCls} mt-1`}
                    data-testid={`pad-field-${selected.key}-${key}`}
                    value={field[key]}
                    onChange={(event) => updatePadField(selected.key, {
                      [key]: Number(event.target.value),
                    })}
                  />
                </label>
              );
              return (
                <div className="min-w-0 sm:col-span-2 rounded-lg border border-slate-200 p-4"
                  data-testid="prescription-field-editor">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="overline text-[#EA580C]">Prescription field overlay</p>
                      <p className="mt-1 text-xs text-slate-500">
                        Configure every patient field independently. Drag its outline in the live preview
                        to move it over the uploaded pad.
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={field.visible !== false}
                        data-testid={`pad-field-${selected.key}-visible`}
                        onChange={(event) => updatePadField(selected.key, {
                          visible: event.target.checked,
                        })}
                      />
                      Visible
                    </label>
                  </div>
                  <div className="mt-4 grid min-w-0 grid-cols-1 gap-3 lg:grid-cols-4">
                    <label className="min-w-0 text-xs text-slate-700">
                      Field
                      <select
                        className={`${inputCls} mt-1`}
                        value={selected.key}
                        data-testid="pad-field-select"
                        onChange={(event) => setActivePadField(event.target.value)}
                      >
                        {PRESCRIPTION_PAD_FIELDS.map((item) => (
                          <option key={item.key} value={item.key}>{item.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="min-w-0 text-xs text-slate-700">
                      Label
                      <input
                        className={`${inputCls} mt-1`}
                        data-testid={`pad-field-${selected.key}-label`}
                        value={field.label}
                        onChange={(event) => updatePadField(selected.key, {
                          label: event.target.value,
                        })}
                      />
                    </label>
                    <label className="min-w-0 text-xs text-slate-700">
                      Label / value
                      <select
                        className={`${inputCls} mt-1`}
                        data-testid={`pad-field-${selected.key}-label-mode`}
                        value={field.label_mode}
                        onChange={(event) => updatePadField(selected.key, {
                          label_mode: event.target.value,
                        })}
                      >
                        <option value="label_value">Label and value</option>
                        <option value="value_only">Value only</option>
                        <option value="label_only">Label only</option>
                      </select>
                    </label>
                    <label className="min-w-0 text-xs text-slate-700">
                      Alignment
                      <select
                        className={`${inputCls} mt-1`}
                        data-testid={`pad-field-${selected.key}-align`}
                        value={field.align}
                        onChange={(event) => updatePadField(selected.key, {
                          align: event.target.value,
                        })}
                      >
                        <option value="left">Left</option>
                        <option value="center">Centre</option>
                        <option value="right">Right</option>
                      </select>
                    </label>
                    {numberField("X position (mm)", "x_mm", 0, PAPER_MM[form.paper_size].w)}
                    {numberField("Y position (mm)", "y_mm", 0, PAPER_MM[form.paper_size].h)}
                    {numberField("Width (mm)", "width_mm", 5, PAPER_MM[form.paper_size].w)}
                    {numberField("Height (mm)", "height_mm", 4, PAPER_MM[form.paper_size].h)}
                    {numberField("Font size (pt)", "font_size_pt", 6, 24)}
                    <label className="min-w-0 text-xs text-slate-700">
                      Font family
                      <select
                        className={`${inputCls} mt-1`}
                        data-testid={`pad-field-${selected.key}-font-family`}
                        value={field.font_family}
                        onChange={(event) => updatePadField(selected.key, {
                          font_family: event.target.value,
                        })}
                      >
                        <option value="">Use document typeface</option>
                        <option value="Georgia, serif">Traditional serif</option>
                        <option value="Verdana, sans-serif">Clinical sans</option>
                        <option value="Courier New, monospace">Monospace</option>
                      </select>
                    </label>
                  </div>
                  <p className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                    <Move className="h-3.5 w-3.5" />
                    Long values stay inside this field’s configured box and cannot move other fields.
                  </p>
                </div>
              );
            })()}

            <div className="sm:col-span-2">
              <p className="overline text-[#EA580C] mb-3">Fields to include</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {meta.fields.map((k) => (
                  <label key={k} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <span className="text-slate-700">{FIELD_LABEL(k)}</span>
                    <input type="checkbox" checked={Boolean(form.fields[k])} data-testid={`template-field-${k}`}
                      onChange={(e) => setForm({ ...form, fields: { ...form.fields, [k]: e.target.checked } })}
                      className="h-4 w-4 accent-[#EA580C]" />
                  </label>
                ))}
              </div>
            </div>

            <div className="sm:col-span-2 flex flex-wrap items-center gap-6">
              {module === "lab" && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={Boolean(form.show_qr)} className="h-4 w-4 accent-[#EA580C]"
                    onChange={(e) => setForm({ ...form, show_qr: e.target.checked })} data-testid="template-show-qr" />
                  Show QR verification
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={Boolean(form.active)} className="h-4 w-4 accent-[#EA580C]"
                  onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="template-active" />
                Active
              </label>
            </div>
            <div className="sm:col-span-2 flex gap-3">
              <button className={primaryBtnCls} data-testid="template-submit">{editing ? "Save changes" : "Create template"}</button>
              <button type="button" onClick={() => setOpen(false)} className={ghostBtnCls}>Cancel</button>
            </div>

            {module === "hospital" && (
              <div className="sm:col-span-2 border-t border-slate-200 pt-4" data-testid="template-live-preview">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <p className="overline text-[#EA580C]">Live preview — matches printed {form.paper_size}</p>
                  <div className="flex gap-1.5">
                    {[["blank", "Blank"], ["partial", "Partial"], ["digital", "Full"]].map(([m, lbl]) => (
                      <button type="button" key={m} data-testid={`preview-mode-${m}`}
                        onClick={() => setForm({ ...form, prescription_mode: m })}
                        className={`text-xs px-2.5 py-1 rounded-full border ${form.prescription_mode === m ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-600 hover:border-slate-900"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
                {PRESCRIPTION_TYPES.includes(form.doc_type) ? (
                  form.use_background && form.letterhead_image ? (
                    <div className="overflow-auto rounded-lg border border-slate-300 bg-slate-200/60 p-4" style={{ maxHeight: 560 }}>
                      <div className="mx-auto w-fit shadow-lg">
                        <RxPad
                          template={{
                            ...form,
                            layout: form.layout?.zones
                              ? form.layout
                              : defaultLayout(form.paper_size),
                          }}
                          editable
                          onFieldLayoutChange={updatePadField}
                          {...sampleRx(form.prescription_mode, form.doc_type, business)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-auto rounded-lg border border-slate-300 bg-slate-200/60 p-4"
                      style={{ maxHeight: 560 }}>
                      <div className="mx-auto w-fit shadow-lg">
                        <RxPad
                          template={{
                            ...form,
                            layout: form.layout?.zones
                              ? form.layout
                              : defaultLayout(form.paper_size),
                          }}
                          editable
                          onFieldLayoutChange={updatePadField}
                          {...sampleRx(form.prescription_mode, form.doc_type, business)}
                        />
                      </div>
                    </div>
                  )
                ) : form.doc_type === "ipd_document" || form.doc_type === "case_sheet" ? (
                  <DesignerSurface paperSize={form.paper_size} template={form}>
                    <CaseSheetDoc data={{ ...SAMPLE_CASE, business }} template={form}
                      docTitle={form.doc_type === "ipd_document" ? "IPD Clinical Document" : "IPD Case Sheet"} />
                  </DesignerSurface>
                ) : form.doc_type === "discharge_summary" ? (
                  <DesignerSurface paperSize={form.paper_size} template={form}>
                    <DischargeSummaryDoc data={{ ...SAMPLE_CASE, business }} template={form} />
                  </DesignerSurface>
                ) : (
                  <p className="text-xs text-slate-500">
                    Live preview is available for the selected hospital document type.
                  </p>
                )}
                <p className="text-[11px] text-slate-500 mt-2">
                  {form.use_background && form.letterhead_image
                    ? "This preview uses your uploaded pad as the page background — the printed PDF uses the exact same coordinates."
                    : "Toggle logo, letterhead, header/footer and fields above to see the printed document update instantly."}
                </p>
              </div>
            )}
          </form>
        )}
      </Modal>
    </div>
  );
}
